import { useEffect, useState } from 'react'
import { PageHeading } from '@/components/Layout.tsx'
import { Sprig, LeafDivider, Seedling } from '@/components/Botanical.tsx'
import { Heart, Refresh } from '@/components/Icons.tsx'
import { useNames, usePicker, genderWord, type Filter } from '@/features/names/useNames.ts'
import { NameEntry } from '@/features/names/NameEntry.tsx'
import { useFavorites } from '@/features/names/useFavorites.ts'
import { useAuth } from '@/state/AuthContext.tsx'
import { ordinal, initials } from '@/lib/format.ts'
import type { Gender } from '@/lib/types.ts'

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'any', label: 'Anyone' },
  { id: 'girl', label: 'Girls' },
  { id: 'boy', label: 'Boys' },
]

const GENDER_TINT: Record<Gender, string> = {
  girl: 'var(--c-terracotta)',
  boy: 'var(--c-moss)',
  neutral: 'var(--c-gold)',
}

export default function Names() {
  const { user } = useAuth()
  const [filter, setFilter] = useState<Filter>('any')
  const { all, loading, error } = useNames()
  const { current, next, select, poolSize } = usePicker(all, filter)
  const { groups, isFavorite, toggle, loading: favLoading } = useFavorites(user?.id)

  // Space bar draws another name — the whole point is to keep going.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (el && /^(INPUT|TEXTAREA)$/.test(el.tagName)) return
      if (e.code === 'Space') { e.preventDefault(); next() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [next])

  const loved = current ? isFavorite(current.name) : false

  return (
    <>
      <PageHeading eyebrow="Three and a half thousand of them" title="Names">
        One at a time, so you actually hear each one. Press space, or the button, for another.
      </PageHeading>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-linen bg-paper/60 p-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={[
                'rounded-full px-5 py-1.5 text-[0.83rem] transition-all duration-300 ease-botanical',
                filter === f.id ? 'bg-moss text-paper shadow-sm' : 'text-quiet hover:text-fern',
              ].join(' ')}
            >
              {f.label}
            </button>
          ))}
        </div>
        {poolSize > 0 && (
          <span className="text-[0.76rem] text-whisper">{poolSize.toLocaleString()} to choose from</span>
        )}
      </div>

      <div className="mt-4">
        <NameEntry all={all} filter={filter} onPick={select} />
      </div>

      {/* The name itself */}
      <section className="relative mt-10 flex min-h-[24rem] flex-col items-center justify-center overflow-hidden rounded-leaf border border-linen bg-paper/55 px-6 py-16 text-center backdrop-blur-[2px]">
        <Sprig tone="var(--c-sage)" className="pointer-events-none absolute -left-2 bottom-0 h-40 w-auto opacity-25" aria-hidden />
        <Sprig tone="var(--c-clay)" className="pointer-events-none absolute -right-2 top-0 h-32 w-auto rotate-180 opacity-20" aria-hidden />

        {loading && <p className="text-whisper">Gathering the names…</p>}
        {error && <p className="text-terracotta">{error}</p>}

        {current && (
          <div key={current.name} className="animate-unfurl">
            <p className="eyebrow" style={{ color: GENDER_TINT[current.gender] }}>
              {genderWord[current.gender]}
            </p>

            <h2
              className="mt-4 font-display text-[3.4rem] leading-[1.02] sm:text-[5rem]"
              style={{ fontVariationSettings: "'SOFT' 55, 'WONK' 1" }}
            >
              {current.name}
            </h2>

            {current.meaning ? (
              <p className="prose-soft mx-auto mt-5 max-w-[30rem] text-[1.05rem] italic">
                <span className="not-italic text-whisper">{current.origin}</span>
                {' — '}
                {current.meaning}
              </p>
            ) : current.custom ? (
              <p className="mt-5 text-[0.88rem] text-whisper">
                Not one of our three and a half thousand — but it&rsquo;s yours to keep.
              </p>
            ) : (
              <p className="mt-5 text-[0.88rem] text-whisper">We have not written this one&rsquo;s story down yet.</p>
            )}

            {current.rank && (
              <p className="mt-3 text-[0.78rem] tracking-wide text-whisper">
                {ordinal(current.rank)} most given in 2024
              </p>
            )}

            <div className="mt-9 flex items-center justify-center gap-3">
              <button onClick={next} className="btn-primary">
                <Refresh className="h-4 w-4" />
                Another
              </button>

              <button
                onClick={() => void toggle(current.name, current.gender)}
                aria-pressed={loved}
                aria-label={loved ? `Remove ${current.name} from favourites` : `Keep ${current.name}`}
                className={[
                  'btn border transition-all duration-300',
                  loved
                    ? 'border-terracotta/50 bg-blush/40 text-terracotta'
                    : 'border-linen bg-paper/50 text-quiet hover:border-clay hover:text-terracotta',
                ].join(' ')}
              >
                <Heart filled={loved} className="h-4 w-4" />
                {loved ? 'Kept' : 'Keep this one'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Favourites */}
      <section className="mt-20">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <p className="eyebrow">Kept</p>
            <h2 className="mt-2 font-display text-[1.9rem]">The shortlist</h2>
          </div>
          {groups.length > 0 && (
            <span className="text-[0.76rem] text-whisper">
              {groups.length} name{groups.length === 1 ? '' : 's'}
              {groups.some((g) => g.mutual) && ` · ${groups.filter((g) => g.mutual).length} you both love`}
            </span>
          )}
        </div>

        <LeafDivider tone="var(--c-sage)" className="mt-5 h-4 w-full opacity-35" />

        {favLoading ? (
          <p className="mt-8 text-whisper">Looking…</p>
        ) : groups.length === 0 ? (
          <div className="mt-10 flex flex-col items-center gap-4 py-10 text-center">
            <Seedling tone="var(--c-sage)" className="h-16 w-auto opacity-50" />
            <p className="prose-soft max-w-[22rem]">
              Nothing kept yet. When one lands, press <span className="italic">keep this one</span> and it will wait for you here.
            </p>
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-linen">
            {groups.map((g) => (
              <li key={g.name} className="group flex items-center gap-4 py-3.5">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: GENDER_TINT[g.gender] }} aria-hidden />

                <span className="font-display text-[1.35rem] leading-none">{g.name}</span>

                {g.mutual && (
                  <span className="rounded-full bg-blush/50 px-2.5 py-0.5 text-[0.64rem] uppercase tracking-[0.16em] text-terracotta">
                    both of you
                  </span>
                )}

                <span className="ml-auto flex items-center gap-1.5">
                  {g.admirers.map((a) => (
                    <span
                      key={a.id}
                      title={a.displayName}
                      className="grid h-6 w-6 place-items-center rounded-full text-[0.55rem] tracking-wider text-paper"
                      style={{ background: a.accent }}
                    >
                      {initials(a.displayName)}
                    </span>
                  ))}
                </span>

                <button
                  onClick={() => void toggle(g.name, g.gender)}
                  aria-label={isFavorite(g.name) ? `Remove ${g.name}` : `Keep ${g.name}`}
                  className={[
                    'shrink-0 rounded-full p-1.5 transition-all duration-200',
                    isFavorite(g.name)
                      ? 'text-terracotta hover:bg-blush/40'
                      : 'text-whisper opacity-0 hover:text-terracotta focus-visible:opacity-100 group-hover:opacity-100',
                  ].join(' ')}
                >
                  <Heart filled={isFavorite(g.name)} className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}
